import React, { Component } from 'react';
import { Menu } from '../../components/Menu/Menu';
import LoadingWindow from '../../components/LoadingWindow';
import { confirmCloseAlert } from '../../functions/alerts';
import { getInformationWithData } from '../../services/CABE';
import { getValueCookie } from '../../services/cookieService';
import { DateFormatMAS90 } from '../../functions/dateFormat';
import { NumberFormat } from '../../functions/generalFunctions';
import { pricesFormat } from '../../functions/pricesFormat';
import { BsBoxSeam, BsArrowRepeat, BsSortDown, BsSortUp, BsChevronDown, BsChevronRight, BsExclamationTriangle } from 'react-icons/bs';
import { AiOutlineSearch } from 'react-icons/ai';
import { IoCloseCircle } from 'react-icons/io5';
import styles from '../../css/SalesOrdersAdminV3.module.css';

/**
 * SalesOrdersAdminV3 — vista de administrador (solo lectura) de /sales-orders-v3 (2026-10-28).
 *
 * Trae lo mismo que el botón "By SO List" de SalesOrderV3 (SalesOrderList.js →
 * /sales/get/header; el prefijo /my, /alt o /ne lo pone getInformationWithData
 * según la cookie apiPrefix de la sesión), pero siempre con SalespersonNo '0000'
 * para que el back no filtre por vendedor. Separa Standard / Quote, agrupa por
 * SalespersonNo con subtotal y suma NonTaxableAmt. Sin acciones de escritura.
 * Solo TypeUser '1'.
 *
 * Resumen ejecutivo (2026-10-28, Prioridad 1): total abierto, Standard por
 * status, antigüedad + alertas, ranking de vendedores, top clientes y filtros
 * por chips. Todo se calcula en el front con los mismos datos.
 */

const fmtMoney = (n) => '$' + NumberFormat(pricesFormat(n));
const fmtPct = (n) => (Number.isFinite(n) ? n.toFixed(1) : '0.0') + '%';
const amountOf = (o) => Number(o.NonTaxableAmt) || 0;
const statusOf = (o) => (o.Status === 1 || o.Status === 2 ? o.Status : 0);
const sumOf = (list) => list.reduce((acc, o) => acc + amountOf(o), 0);
const spOf = (o) => String(o.SalespersonNo || '').trim() || '—';

const STATUS = {
    0: { label: 'Not started', cls: 'statusNotStarted', seg: 'segNotStarted' },
    1: { label: 'In process', cls: 'statusInProcess', seg: 'segInProcess' },
    2: { label: 'Finished', cls: 'statusFinished', seg: 'segFinished' },
};

const SORT_OPTIONS = [
    { value: 'SalespersonNo', label: 'Sales Person' },
    { value: 'SalesOrderNo', label: 'Order No' },
    { value: 'CustomerNo', label: 'Customer No' },
    { value: 'Status', label: 'Status' },
    { value: 'BillToName', label: 'Customer Name' },
];

// Antigüedad = días desde OrderDate (YYYY-MM-DD de MAS90) hasta hoy.
const AGE_BUCKETS = [
    { key: '0-1', label: '0–1 days', min: 0, max: 1, cls: 'age0' },
    { key: '2-3', label: '2–3 days', min: 2, max: 3, cls: 'age1' },
    { key: '4-7', label: '4–7 days', min: 4, max: 7, cls: 'age2' },
    { key: '8+', label: '8+ days', min: 8, max: Infinity, cls: 'age3' },
];

// Alertas: cada una fija la sección y filtra la lista al hacer clic.
const ALERTS = [
    { id: 'notStarted', section: 'S', label: 'Standard not started', hint: 'more than 2 days', test: (o, d) => statusOf(o) === 0 && d > 2 },
    { id: 'readyToInvoice', section: 'S', label: 'Finished, not invoiced', hint: 'more than 1 day', test: (o, d) => statusOf(o) === 2 && d > 1 },
    { id: 'oldQuotes', section: 'Q', label: 'Old quotes', hint: 'more than 14 days', test: (o, d) => d > 14 },
];

function startOfToday() {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), t.getDate());
}

function daysOpen(o, today) {
    const parts = String(o.OrderDate || '').slice(0, 10).split('-');
    if (parts.length !== 3) return null;
    const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    if (isNaN(d.getTime())) return null;
    return Math.max(0, Math.round((today - d) / 86400000));
}

const bucketOf = (days) => (days === null ? null : AGE_BUCKETS.find(b => days >= b.min && days <= b.max));

const compareText = (a, b) => String(a || '').localeCompare(String(b || ''), undefined, { numeric: true, sensitivity: 'base' });

function compareBy(key) {
    if (key === 'Status') return (a, b) => statusOf(a) - statusOf(b);
    return (a, b) => compareText(a[key], b[key]);
}

// Agrupa por SalespersonNo y ordena grupos y filas según sortKey/sortDir.
function groupBySalesperson(orders, sortKey, sortDir) {
    const dir = sortDir === 'asc' ? 1 : -1;
    const map = {};
    for (const o of orders) {
        const sp = spOf(o);
        if (!map[sp]) map[sp] = { salesperson: sp, orders: [], total: 0 };
        map[sp].orders.push(o);
        map[sp].total += amountOf(o);
    }
    const groups = Object.values(map);
    // Con sort por SalesPerson la dirección aplica a los grupos y las filas van por Order No.
    const rowCmp = sortKey === 'SalespersonNo' ? compareBy('SalesOrderNo') : compareBy(sortKey);
    const rowDir = sortKey === 'SalespersonNo' ? 1 : dir;
    const groupDir = sortKey === 'SalespersonNo' ? dir : 1;
    for (const g of groups) g.orders.sort((a, b) => rowDir * rowCmp(a, b));
    groups.sort((a, b) => groupDir * compareText(a.salesperson, b.salesperson));
    return groups;
}

// Ranking por vendedor de la sección; Q/S cuenta sobre todas las órdenes abiertas.
function salespersonRanking(sectionOrders, allOrders) {
    const total = sumOf(sectionOrders);
    const map = {};
    for (const o of sectionOrders) {
        const sp = spOf(o);
        if (!map[sp]) map[sp] = { salesperson: sp, amount: 0, count: 0, customers: new Set() };
        map[sp].amount += amountOf(o);
        map[sp].count += 1;
        map[sp].customers.add(o.CustomerNo);
    }
    const mix = {};
    for (const o of allOrders) {
        const sp = spOf(o);
        if (!mix[sp]) mix[sp] = { q: 0, s: 0 };
        if (o.OrderType === 'S') mix[sp].s += 1; else mix[sp].q += 1;
    }
    return Object.values(map)
        .map(r => ({
            ...r,
            customers: r.customers.size,
            avg: r.count ? r.amount / r.count : 0,
            share: total ? (r.amount / total) * 100 : 0,
            quotes: mix[r.salesperson] ? mix[r.salesperson].q : 0,
            standards: mix[r.salesperson] ? mix[r.salesperson].s : 0,
        }))
        .sort((a, b) => b.amount - a.amount);
}

function topCustomers(sectionOrders, limit) {
    const total = sumOf(sectionOrders);
    const map = {};
    for (const o of sectionOrders) {
        const key = String(o.CustomerNo || '').trim();
        if (!map[key]) map[key] = { customerNo: key, name: o.BillToName, amount: 0, count: 0 };
        map[key].amount += amountOf(o);
        map[key].count += 1;
    }
    const all = Object.values(map).sort((a, b) => b.amount - a.amount);
    const top5 = all.slice(0, 5).reduce((acc, c) => acc + c.amount, 0);
    return {
        list: all.slice(0, limit).map(c => ({ ...c, share: total ? (c.amount / total) * 100 : 0 })),
        customers: all.length,
        top5Share: total ? (top5 / total) * 100 : 0,
    };
}

export default class SalesOrdersAdminV3 extends Component {
    constructor(props) {
        super(props);
        this.ModalLoading = React.createRef();
        this.state = {
            isAdmin: getValueCookie('TypeUser') === '1',
            orders: [],
            loaded: false,
            lastUpdated: null,
            section: 'S',
            search: '',
            sortKey: 'SalespersonNo',
            sortDir: 'asc',
            collapsed: {},
            statusFilter: 'all',
            ageFilter: 'all',
            spFilter: '',
            alertFilter: '',
        };
    }

    componentDidMount() {
        if (this.state.isAdmin) this.load();
    }

    load = async () => {
        const idCompany = getValueCookie('SubCompanyId') ? Number(getValueCookie('SubCompanyId')) : Number(getValueCookie('CompanyId'));
        this.ModalLoading.current.showState(true);
        try {
            const resp = await getInformationWithData('/sales/get/header', { SalespersonNo: '0000', idCompany });
            if (resp && resp.status && resp.status.code === 1) {
                this.setState({ orders: Array.isArray(resp.data) ? resp.data : [], loaded: true, lastUpdated: new Date() });
            } else {
                confirmCloseAlert('incorrect', 'Could not load the sales orders. Try again please.');
            }
        } catch (e) {
            confirmCloseAlert('incorrect', 'Could not load the sales orders. Try again please.');
        }
        this.ModalLoading.current.showState(false);
    };

    // Cambiar de sección limpia los filtros que solo tienen sentido en la otra.
    setSection = (section, extra, scroll) => {
        const next = section === this.state.section ? {} : { section, statusFilter: 'all', alertFilter: '' };
        this.setState({ ...next, ...(extra || {}) }, () => { if (scroll) this.scrollToList(); });
    };

    applyAlert = (alert) => {
        const active = this.state.alertFilter === alert.id;
        this.setState({
            section: alert.section,
            alertFilter: active ? '' : alert.id,
            statusFilter: 'all',
            ageFilter: 'all',
        }, () => { if (!active) this.scrollToList(); });
    };

    pickSalesperson = (sp) => {
        const active = this.state.spFilter === sp;
        this.setState({ spFilter: active ? '' : sp }, () => { if (!active) this.scrollToList(); });
    };

    pickAge = (key) => {
        const active = this.state.ageFilter === key;
        this.setState({ ageFilter: active ? 'all' : key, alertFilter: '' }, () => { if (!active) this.scrollToList(); });
    };

    scrollToList() {
        const el = document.getElementById('soAdminList');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    clearFilters = () => {
        this.setState({ statusFilter: 'all', ageFilter: 'all', spFilter: '', alertFilter: '', search: '' });
    };

    toggleGroup = (key) => {
        this.setState(prev => ({ collapsed: { ...prev.collapsed, [key]: !prev.collapsed[key] } }));
    };

    matches(o, q) {
        if (!q) return true;
        return [o.SalesOrderNo, o.CustomerNo, o.BillToName, o.SalespersonNo, o.TermsCode]
            .some(v => String(v || '').toLowerCase().includes(q));
    }

    renderStatus(o) {
        const s = STATUS[statusOf(o)];
        return <span className={`${styles.status} ${styles[s.cls]}`}>{s.label}</span>;
    }

    renderDays(days) {
        if (days === null) return <span className={styles.days}>—</span>;
        const b = bucketOf(days);
        return <span className={`${styles.days} ${styles[b.cls]}`}>{days}d</span>;
    }

    renderChip(active, label, onClick, key) {
        return (
            <button key={key} type="button" className={`${styles.chip} ${active ? styles.chipActive : ''}`} aria-pressed={active} onClick={onClick}>
                {label}
            </button>
        );
    }

    renderGroup(g, today) {
        const { section, collapsed } = this.state;
        const key = section + '|' + g.salesperson;
        const isCollapsed = !!collapsed[key];
        return (
            <section key={key} className={styles.group}>
                <button type="button" className={styles.groupHeader} onClick={() => this.toggleGroup(key)} aria-expanded={!isCollapsed}>
                    <span className={styles.groupChevron}>{isCollapsed ? <BsChevronRight /> : <BsChevronDown />}</span>
                    <span className={styles.groupName}>{g.salesperson}</span>
                    <span className={styles.groupCount}>{g.orders.length} {g.orders.length === 1 ? 'order' : 'orders'}</span>
                    <span className={styles.groupTotal}>{fmtMoney(g.total)}</span>
                </button>

                {!isCollapsed && (
                    <>
                        {/* Mobile — tarjetas */}
                        <ul className={styles.cards}>
                            {g.orders.map(o => (
                                <li key={o.SalesOrderNo} className={styles.card}>
                                    <div className={styles.cardTop}>
                                        <span className={styles.orderNo}>{o.SalesOrderNo}</span>
                                        {this.renderStatus(o)}
                                    </div>
                                    <div className={styles.cardCustomer}>{o.BillToName}</div>
                                    <div className={styles.cardMeta}>
                                        <span>{o.CustomerNo}</span>
                                        <span>{DateFormatMAS90(o.OrderDate)}</span>
                                        {this.renderDays(daysOpen(o, today))}
                                    </div>
                                    <div className={styles.cardAmount}>{fmtMoney(amountOf(o))}</div>
                                </li>
                            ))}
                        </ul>

                        {/* Desktop — tabla */}
                        <div className={styles.tableWrap}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Order No</th>
                                        <th>Status</th>
                                        <th>Customer No</th>
                                        <th>Customer Name</th>
                                        <th>Terms</th>
                                        <th>Date</th>
                                        <th>Days Open</th>
                                        <th className={styles.num}>Total Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {g.orders.map(o => (
                                        <tr key={o.SalesOrderNo}>
                                            <td className={styles.orderNo}>{o.SalesOrderNo}</td>
                                            <td>{this.renderStatus(o)}</td>
                                            <td>{o.CustomerNo}</td>
                                            <td>{o.BillToName}</td>
                                            <td>{o.TermsCode}</td>
                                            <td>{DateFormatMAS90(o.OrderDate)}</td>
                                            <td>{this.renderDays(daysOpen(o, today))}</td>
                                            <td className={styles.num}>{fmtMoney(amountOf(o))}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </section>
        );
    }

    render() {
        const { isAdmin, orders, loaded, lastUpdated, section, search, sortKey, sortDir, statusFilter, ageFilter, spFilter, alertFilter } = this.state;

        if (!isAdmin) {
            return (
                <div className={styles.page}>
                    <Menu />
                    <div className={styles.denied}>This view is only available to administrators.</div>
                </div>
            );
        }

        const today = startOfToday();
        const standard = orders.filter(o => o.OrderType === 'S');
        const quote = orders.filter(o => o.OrderType !== 'S');
        const sectionOrders = section === 'S' ? standard : quote;
        const sectionName = section === 'S' ? 'Standard' : 'Quote';

        // ── Resumen ejecutivo ────────────────────────────────────────────
        const totalOpen = sumOf(orders);
        const standardTotal = sumOf(standard);
        const byStatus = [0, 1, 2].map(s => {
            const list = standard.filter(o => statusOf(o) === s);
            const amount = sumOf(list);
            return { status: s, amount, count: list.length, share: standardTotal ? (amount / standardTotal) * 100 : 0 };
        });
        const customerCount = new Set(orders.map(o => String(o.CustomerNo || '').trim())).size;
        const avgTicket = orders.length ? totalOpen / orders.length : 0;

        // ── Alertas y antigüedad ─────────────────────────────────────────
        const alerts = ALERTS.map(a => {
            const list = (a.section === 'S' ? standard : quote).filter(o => {
                const d = daysOpen(o, today);
                return d !== null && a.test(o, d);
            });
            return { ...a, count: list.length, amount: sumOf(list) };
        });
        const sectionTotal = sumOf(sectionOrders);
        const aging = AGE_BUCKETS.map(b => {
            const list = sectionOrders.filter(o => bucketOf(daysOpen(o, today)) === b);
            const amount = sumOf(list);
            return { ...b, count: list.length, amount, share: sectionTotal ? (amount / sectionTotal) * 100 : 0 };
        });

        // ── Ranking y clientes ───────────────────────────────────────────
        const ranking = salespersonRanking(sectionOrders, orders);
        const maxShare = ranking.length ? ranking[0].share : 0;
        const customers = topCustomers(sectionOrders, 10);

        // ── Lista filtrada ───────────────────────────────────────────────
        const activeAlert = ALERTS.find(a => a.id === alertFilter);
        const q = search.trim().toLowerCase();
        const visible = sectionOrders.filter(o => {
            const d = daysOpen(o, today);
            if (statusFilter !== 'all' && statusOf(o) !== statusFilter) return false;
            if (ageFilter !== 'all') {
                const b = bucketOf(d);
                if (!b || b.key !== ageFilter) return false;
            }
            if (spFilter && spOf(o) !== spFilter) return false;
            if (activeAlert && (d === null || !activeAlert.test(o, d))) return false;
            return this.matches(o, q);
        });
        const groups = groupBySalesperson(visible, sortKey, sortDir);
        const hasFilters = statusFilter !== 'all' || ageFilter !== 'all' || spFilter || alertFilter || q;

        return (
            <div className={styles.page}>
                <Menu />
                <LoadingWindow ref={this.ModalLoading} />

                {/* Hero */}
                <div className={styles.hero}>
                    <div className={styles.heroLeft}>
                        <div className={styles.heroIconBox}><BsBoxSeam className={styles.heroIcon} /></div>
                        <div>
                            <h1 className={styles.heroTitle}>Sales Orders — Admin</h1>
                            <p className={styles.heroSubtitle}>
                                {orders.length} open orders{lastUpdated ? ` · Updated ${lastUpdated.toLocaleTimeString()}` : ''}
                            </p>
                        </div>
                    </div>
                    <button type="button" className={styles.heroBtn} onClick={this.load}>
                        <BsArrowRepeat /> Refresh
                    </button>
                </div>

                <div className={styles.body}>
                    {/* KPI — Standard / Quote también funcionan como selector de sección */}
                    <div className={styles.kpis}>
                        <div className={`${styles.kpi} ${styles.kpiTotal}`}>
                            <span className={styles.kpiLabel}>Total open</span>
                            <span className={styles.kpiValue}>{fmtMoney(totalOpen)}</span>
                            <span className={styles.kpiSub}>{orders.length} orders · {customerCount} customers · avg {fmtMoney(avgTicket)}</span>
                        </div>
                        <button type="button" aria-pressed={section === 'S'}
                            className={`${styles.kpi} ${styles.kpiStandard} ${section === 'S' ? styles.kpiActive : ''}`}
                            onClick={() => this.setSection('S')}>
                            <span className={styles.kpiLabel}>Standard</span>
                            <span className={styles.kpiValue}>{fmtMoney(standardTotal)}</span>
                            <span className={styles.kpiSub}>{standard.length} orders</span>
                        </button>
                        <button type="button" aria-pressed={section === 'Q'}
                            className={`${styles.kpi} ${styles.kpiQuote} ${section === 'Q' ? styles.kpiActive : ''}`}
                            onClick={() => this.setSection('Q')}>
                            <span className={styles.kpiLabel}>Quote</span>
                            <span className={styles.kpiValue}>{fmtMoney(sumOf(quote))}</span>
                            <span className={styles.kpiSub}>{quote.length} orders</span>
                        </button>
                    </div>

                    {/* Standard por status */}
                    <div className={styles.panel}>
                        <div className={styles.panelHeader}>
                            <span className={styles.panelTitle}>Standard pipeline by status</span>
                            <span className={styles.readyBadge}>Ready to invoice: {fmtMoney(byStatus[2].amount)}</span>
                        </div>
                        <div className={styles.stackBar} role="img"
                            aria-label={byStatus.map(s => `${STATUS[s.status].label} ${fmtPct(s.share)}`).join(', ')}>
                            {byStatus.map(s => s.share > 0 && (
                                <div key={s.status} className={`${styles.stackSeg} ${styles[STATUS[s.status].seg]}`} style={{ width: s.share + '%' }} />
                            ))}
                        </div>
                        <div className={styles.legend}>
                            {byStatus.map(s => (
                                <button key={s.status} type="button"
                                    className={`${styles.legendItem} ${section === 'S' && statusFilter === s.status ? styles.legendActive : ''}`}
                                    onClick={() => this.setSection('S', { statusFilter: s.status, alertFilter: '' }, true)}>
                                    <span className={`${styles.legendDot} ${styles[STATUS[s.status].seg]}`} />
                                    <span className={styles.legendLabel}>{STATUS[s.status].label}</span>
                                    <span className={styles.legendValue}>{fmtMoney(s.amount)}</span>
                                    <span className={styles.legendSub}>{s.count} orders · {fmtPct(s.share)}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Alertas */}
                    <div className={styles.alerts}>
                        {alerts.map(a => (
                            <button key={a.id} type="button"
                                className={`${styles.alert} ${a.count ? styles.alertOn : styles.alertOff} ${alertFilter === a.id ? styles.alertActive : ''}`}
                                onClick={() => this.applyAlert(a)} disabled={!a.count} aria-pressed={alertFilter === a.id}>
                                <BsExclamationTriangle className={styles.alertIcon} />
                                <span className={styles.alertText}>
                                    <span className={styles.alertLabel}>{a.label}</span>
                                    <span className={styles.alertHint}>{a.hint}</span>
                                </span>
                                <span className={styles.alertNumbers}>
                                    <span className={styles.alertCount}>{a.count}</span>
                                    <span className={styles.alertAmount}>{fmtMoney(a.amount)}</span>
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Antigüedad de la sección */}
                    <div className={styles.panel}>
                        <div className={styles.panelHeader}>
                            <span className={styles.panelTitle}>{sectionName} aging</span>
                            <span className={styles.panelMeta}>days since order date</span>
                        </div>
                        <div className={styles.agingGrid}>
                            {aging.map(b => (
                                <button key={b.key} type="button"
                                    className={`${styles.agingCell} ${styles[b.cls + 'Cell']} ${ageFilter === b.key ? styles.agingActive : ''}`}
                                    aria-pressed={ageFilter === b.key} onClick={() => this.pickAge(b.key)}>
                                    <span className={styles.agingLabel}>{b.label}</span>
                                    <span className={styles.agingValue}>{fmtMoney(b.amount)}</span>
                                    <span className={styles.agingSub}>{b.count} orders · {fmtPct(b.share)}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={styles.split}>
                        {/* Ranking de vendedores */}
                        <div className={styles.panel}>
                            <div className={styles.panelHeader}>
                                <span className={styles.panelTitle}>{sectionName} by salesperson</span>
                                <span className={styles.panelMeta}>{ranking.length} salespersons</span>
                            </div>
                            {ranking.length === 0 && <div className={styles.panelEmpty}>No data</div>}
                            <ol className={styles.rankList}>
                                {ranking.map((r, i) => (
                                    <li key={r.salesperson}>
                                        <button type="button" className={`${styles.rankRow} ${spFilter === r.salesperson ? styles.rankActive : ''}`}
                                            aria-pressed={spFilter === r.salesperson} onClick={() => this.pickSalesperson(r.salesperson)}>
                                            <span className={styles.rankPos}>{i + 1}</span>
                                            <span className={styles.rankMain}>
                                                <span className={styles.rankTop}>
                                                    <span className={styles.rankName}>{r.salesperson}</span>
                                                    <span className={styles.rankAmount}>{fmtMoney(r.amount)}</span>
                                                </span>
                                                <span className={styles.rankBarTrack}>
                                                    <span className={styles.rankBar} style={{ width: (maxShare ? (r.share / maxShare) * 100 : 0) + '%' }} />
                                                </span>
                                                <span className={styles.rankMeta}>
                                                    {fmtPct(r.share)} · {r.count} orders · {r.customers} customers · avg {fmtMoney(r.avg)} · Q {r.quotes} / S {r.standards}
                                                </span>
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ol>
                        </div>

                        {/* Top clientes */}
                        <div className={styles.panel}>
                            <div className={styles.panelHeader}>
                                <span className={styles.panelTitle}>Top {sectionName} customers</span>
                                <span className={styles.panelMeta}>Top 5 = <strong>{fmtPct(customers.top5Share)}</strong> of {customers.customers} customers</span>
                            </div>
                            {customers.list.length === 0 && <div className={styles.panelEmpty}>No data</div>}
                            <ol className={styles.custList}>
                                {customers.list.map((c, i) => (
                                    <li key={c.customerNo} className={styles.custRow}>
                                        <span className={styles.rankPos}>{i + 1}</span>
                                        <span className={styles.custMain}>
                                            <span className={styles.custName}>{c.name}</span>
                                            <span className={styles.custMeta}>{c.customerNo} · {c.count} {c.count === 1 ? 'order' : 'orders'}</span>
                                        </span>
                                        <span className={styles.custNumbers}>
                                            <span className={styles.custAmount}>{fmtMoney(c.amount)}</span>
                                            <span className={styles.custShare}>{fmtPct(c.share)}</span>
                                        </span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </div>

                    {/* Lista: filtros + búsqueda + orden */}
                    <div id="soAdminList" className={styles.listAnchor} />
                    <div className={styles.filters}>
                        <div className={styles.chipRow}>
                            <span className={styles.chipLabel}>Status</span>
                            {this.renderChip(statusFilter === 'all', 'All', () => this.setState({ statusFilter: 'all' }), 'all')}
                            {[0, 1, 2].map(s => this.renderChip(statusFilter === s, STATUS[s].label,
                                () => this.setState({ statusFilter: statusFilter === s ? 'all' : s }), 's' + s))}
                        </div>
                        <div className={styles.chipRow}>
                            <span className={styles.chipLabel}>Age</span>
                            {this.renderChip(ageFilter === 'all', 'All', () => this.setState({ ageFilter: 'all' }), 'all')}
                            {AGE_BUCKETS.map(b => this.renderChip(ageFilter === b.key, b.label,
                                () => this.setState({ ageFilter: ageFilter === b.key ? 'all' : b.key }), b.key))}
                        </div>
                        <div className={styles.chipRow}>
                            <span className={styles.chipLabel}>Salesperson</span>
                            {this.renderChip(!spFilter, 'All', () => this.setState({ spFilter: '' }), 'all')}
                            {ranking.map(r => this.renderChip(spFilter === r.salesperson, r.salesperson,
                                () => this.setState({ spFilter: spFilter === r.salesperson ? '' : r.salesperson }), r.salesperson))}
                        </div>
                        {activeAlert && (
                            <div className={styles.chipRow}>
                                <span className={styles.chipLabel}>Alert</span>
                                <button type="button" className={`${styles.chip} ${styles.chipAlert}`} onClick={() => this.setState({ alertFilter: '' })}>
                                    {activeAlert.label} ({activeAlert.hint}) <IoCloseCircle />
                                </button>
                            </div>
                        )}
                    </div>

                    <div className={styles.toolbar}>
                        <div className={styles.searchBox}>
                            <AiOutlineSearch className={styles.searchIcon} />
                            <input
                                className={styles.searchInput}
                                type="text"
                                placeholder="Search order, customer, salesperson..."
                                value={search}
                                onChange={e => this.setState({ search: e.target.value })}
                            />
                            {search && (
                                <IoCloseCircle className={styles.searchClear} title="Clear search" onClick={() => this.setState({ search: '' })} />
                            )}
                        </div>
                        <div className={styles.sortBox}>
                            <label className={styles.sortLabel} htmlFor="soAdminSort">Sort by</label>
                            <select id="soAdminSort" className={styles.sortSelect} value={sortKey}
                                onChange={e => this.setState({ sortKey: e.target.value })}>
                                {SORT_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                            </select>
                            <button type="button" className={styles.sortDirBtn}
                                title={sortDir === 'asc' ? 'Ascending' : 'Descending'}
                                aria-label={sortDir === 'asc' ? 'Ascending' : 'Descending'}
                                onClick={() => this.setState({ sortDir: sortDir === 'asc' ? 'desc' : 'asc' })}>
                                {sortDir === 'asc' ? <BsSortUp /> : <BsSortDown />}
                            </button>
                        </div>
                    </div>

                    {/* Resumen de la lista visible */}
                    <div className={styles.sectionSummary}>
                        <span className={styles.sectionTitle}>{sectionName} orders</span>
                        <span className={styles.sectionMeta}>
                            {visible.length} orders · {groups.length} salespersons · <strong>{fmtMoney(sumOf(visible))}</strong>
                            {hasFilters && <button type="button" className={styles.clearBtn} onClick={this.clearFilters}>Clear filters</button>}
                        </span>
                    </div>

                    {loaded && groups.length === 0 && (
                        <div className={styles.empty}>No {sectionName} orders found</div>
                    )}
                    {groups.map(g => this.renderGroup(g, today))}
                </div>
            </div>
        );
    }
}
